import categories, { getCategory } from "../categories";
import { getAuthor } from "../authors";
import { slugify, normalizeForSearch, readingTimeMinutes, contentToText, truncate } from "../../utils/text";

/**
 * Registre des articles.
 *
 * Chaque article est un fichier `src/blog/data/posts/<slug>.js` exportant par défaut un objet
 * (voir src/blog/README.md pour le format). Les fichiers de ce dossier sont chargés automatiquement :
 * ajouter un article = ajouter un fichier, rien d'autre à déclarer.
 */
const context = require.context("./", false, /^\.\/(?!index\.js$)[^/]+\.js$/);

const isProduction = process.env.NODE_ENV === "production";
const warn = (...args) => {
  if (!isProduction && typeof console !== "undefined") console.warn("[blog]", ...args);
};

const normalizePost = (raw, file) => {
  if (!raw || typeof raw !== "object") {
    warn(`${file} : export par défaut manquant ou invalide, article ignoré.`);
    return null;
  }
  const required = ["slug", "title", "category", "author", "publishedAt", "content"];
  const missing = required.filter(k => raw[k] == null || raw[k] === "");
  if (missing.length) {
    warn(`${file} : champs manquants (${missing.join(", ")}), article ignoré.`);
    return null;
  }
  if (raw.draft && isProduction) return null;

  const category = getCategory(raw.category);
  if (!category) warn(`${file} : catégorie inconnue « ${raw.category} » (valeurs possibles : ${categories.map(c => c.id).join(", ")}).`);
  const author = getAuthor(raw.author);
  if (!author) warn(`${file} : auteur inconnu « ${raw.author} ».`);

  const references = Array.isArray(raw.references) ? raw.references : [];
  const content = Array.isArray(raw.content) ? raw.content : [];
  const tags = Array.isArray(raw.tags) ? raw.tags.filter(Boolean) : [];
  const excerpt = raw.excerpt || truncate(contentToText(content), 180);

  // Contrôle de cohérence des citations (dev uniquement) : chaque [^n] doit exister.
  if (!isProduction) {
    const ids = new Set(references.map(r => String(r.id)));
    const cited = new Set((contentToTextRaw(content).match(/\[\^(\d+)\]/g) || []).map(m => m.replace(/\D/g, "")));
    cited.forEach(n => !ids.has(n) && warn(`${file} : appel de référence [^${n}] sans référence correspondante.`));
    ids.forEach(n => !cited.has(n) && warn(`${file} : la référence ${n} n'est jamais citée dans le texte.`));
  }

  return {
    ...raw,
    slug: slugify(raw.slug),
    excerpt,
    tags,
    tagSlugs: tags.map(slugify),
    category: category || { id: raw.category, slug: slugify(raw.category), label: raw.category, color: "#6415FF" },
    author: author || { id: raw.author, name: raw.author },
    updatedAt: raw.updatedAt || raw.publishedAt,
    references,
    content,
    readingTime: readingTimeMinutes(content),
    featured: Boolean(raw.featured),
    cover: raw.cover || {},
    searchText: normalizeForSearch([raw.title, excerpt, tags.join(" "), contentToText(content)].join(" ")),
  };
};

// Version brute (avec balisage) pour retrouver les [^n] avant nettoyage.
const contentToTextRaw = content =>
  content
    .map(b => [b.title, b.text, ...(b.items || []), ...((b.facts || []).map(f => `${f.label} ${f.value}`))].filter(Boolean).join(" "))
    .join(" ");

const loaded = context
  .keys()
  .map(file => normalizePost(context(file).default, file))
  .filter(Boolean);

// Doublons de slug : on garde le premier et on prévient.
const seen = new Set();
const posts = loaded
  .filter(p => {
    if (seen.has(p.slug)) {
      warn(`slug en double « ${p.slug} », un des articles est ignoré.`);
      return false;
    }
    seen.add(p.slug);
    return true;
  })
  .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt) || a.title.localeCompare(b.title, "fr"));

export default posts;

export const getPostBySlug = slug => posts.find(p => p.slug === slug) || null;

export const getLatestPosts = (count = 3) => posts.slice(0, count);

/** L'article « à la une » : le plus récent marqué `featured`, sinon le plus récent. */
export const getFeaturedPost = () => posts.find(p => p.featured) || posts[0] || null;

export const getPostsByCategory = categoryId => posts.filter(p => p.category.id === categoryId);

export const getPostsByTag = tagSlug => posts.filter(p => p.tagSlugs.includes(tagSlug));

/** Tous les tags avec leur fréquence, du plus utilisé au moins utilisé. */
export const getAllTags = () => {
  const counts = new Map();
  posts.forEach(p =>
    p.tags.forEach((tag, i) => {
      const slug = p.tagSlugs[i];
      const entry = counts.get(slug) || { tag, slug, count: 0 };
      entry.count += 1;
      counts.set(slug, entry);
    })
  );
  return [...counts.values()].sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag, "fr"));
};

/** Articles liés : même catégorie d'abord, puis tags communs, puis les plus récents. */
export const getRelatedPosts = (post, count = 3) => {
  if (!post) return [];
  const score = other => {
    if (other.slug === post.slug) return -1;
    let s = 0;
    if (other.category.id === post.category.id) s += 10;
    s += other.tagSlugs.filter(t => post.tagSlugs.includes(t)).length * 3;
    return s;
  };
  return posts
    .map(p => ({ p, s: score(p) }))
    .filter(x => x.s >= 0)
    .sort((a, b) => b.s - a.s || b.p.publishedAt.localeCompare(a.p.publishedAt))
    .slice(0, count)
    .map(x => x.p);
};

/** Article plus récent / plus ancien dans l'ordre chronologique global. */
export const getAdjacentPosts = post => {
  const index = posts.findIndex(p => p.slug === post.slug);
  if (index === -1) return { newer: null, older: null };
  return { newer: posts[index - 1] || null, older: posts[index + 1] || null };
};

/** Recherche plein texte simple, insensible aux accents et à la casse ; tous les mots doivent apparaître. */
export const searchPosts = (query, source = posts) => {
  const words = normalizeForSearch(query).split(/\s+/).filter(Boolean);
  if (!words.length) return source;
  return source.filter(p => words.every(w => p.searchText.includes(w)));
};

/** Nombre d'articles par catégorie (pour les onglets). */
export const countByCategory = () =>
  Object.fromEntries(categories.map(c => [c.id, posts.filter(p => p.category.id === c.id).length]));
